import { useState, useEffect } from "react";
import { FiPlus, FiUser, FiUserCheck, FiMail, FiUsers, FiSend, FiTrash2, FiShield, FiKey } from "react-icons/fi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import { API } from "../../api/api";

interface UserRole {
    role_id: string;
    role_name: string;
}

interface User {
    user_id: string;
    email: string;
    username: string;
    full_name: string | null;
    customer_id: string | null;
    is_admin: boolean;
    is_active: boolean;
    needs_password_reset: boolean;
    totp_enabled: boolean;
    roles: UserRole[];
    created_at: string | null;
}

interface Customer {
    customer_id: string;
    customer_name: string;
}

const QUANTUM_ORG_ID = process.env.REACT_APP_CUSTOMER_ID || "0402faba-6e11-40e7-9efc-2c8b0e597ce9";

async function fetchUsers(token: string): Promise<User[]> {
    const res = await fetch(`${API}/admin/users`, {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
    });

    if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Failed to fetch users: ${res.status}`);
    }
    return res.json();
}

async function fetchCustomers(token: string): Promise<Customer[]> {
    const res = await fetch(`${API}/admin/customers`, {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
    });

    if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Failed to fetch customers: ${res.status}`);
    }
    return res.json();
}

export default function UsersAll() {
    const { token } = useAuth();
    const queryClient = useQueryClient();

    const [isOpen, setIsOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [fullName, setFullName] = useState("");
    const [customerId, setCustomerId] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [resendingId, setResendingId] = useState<string | null>(null);
    const [resendSuccess, setResendSuccess] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [resetPasswordId, setResetPasswordId] = useState<string | null>(null);
    const [resetPasswordSuccess, setResetPasswordSuccess] = useState<string | null>(null);

    const handleDeleteUser = async (userId: string) => {
        setDeletingId(userId);
        try {
            const res = await fetch(`${API}/admin/users/${userId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || `Delete failed: ${res.status}`);
            }
            queryClient.invalidateQueries({ queryKey: ["users"] });
        } catch (err) {
            setSubmitError((err as Error).message ?? "Failed to delete user");
        } finally {
            setDeletingId(null);
            setConfirmDeleteId(null);
        }
    };

    const handleResendInvite = async (userId: string) => {
        setResendingId(userId);
        setResendSuccess(null);
        try {
            const res = await fetch(`${API}/admin/resend-invite/${userId}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || `Resend failed: ${res.status}`);
            }
            setResendSuccess(userId);
            setTimeout(() => setResendSuccess(null), 3000);
            queryClient.invalidateQueries({ queryKey: ["users"] });
        } catch (err) {
            setSubmitError((err as Error).message ?? "Failed to resend invite");
        } finally {
            setResendingId(null);
        }
    };

    const handleResetPassword = async (userId: string) => {
        setResetPasswordId(userId);
        setResetPasswordSuccess(null);
        try {
            const res = await fetch(`${API}/admin/send-password-reset/${userId}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || `Reset failed: ${res.status}`);
            }
            setResetPasswordSuccess(userId);
            setTimeout(() => setResetPasswordSuccess(null), 3000);
        } catch (err) {
            setSubmitError((err as Error).message ?? "Failed to send password reset");
        } finally {
            setResetPasswordId(null);
        }
    };

    useEffect(() => {
        if (isAdmin) {
            setCustomerId(QUANTUM_ORG_ID);
        }
    }, [isAdmin]);

    const {
        data: users,
        isLoading: isLoadingUsers,
        isFetching: isFetchingUsers,
        error: usersError,
    } = useQuery<User[]>({
        queryKey: ["users"],
        queryFn: () => fetchUsers(token || ""),
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        refetchOnWindowFocus: false,
        enabled: !!token,
    });

    const {
        data: customers,
        isLoading: isLoadingCustomers,
        error: customersError,
    } = useQuery<Customer[]>({
        queryKey: ["customers"],
        queryFn: () => fetchCustomers(token || ""),
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        refetchOnWindowFocus: false,
        enabled: !!token,
    });

    const resetForm = () => {
        setEmail("");
        setFullName("");
        setCustomerId("");
        setIsAdmin(false);
    };

    const handleCancel = () => {
        resetForm();
        setIsOpen(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/admin/invite-user`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    customer_id: customerId,
                    email,
                    full_name: fullName || null,
                    is_admin: isAdmin,
                }),
            });

            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || `Invite failed: ${res.status}`);
            }

            queryClient.invalidateQueries({ queryKey: ["users"] });

            resetForm();
            setIsOpen(false);
        } catch (err) {
            setSubmitError((err as Error).message ?? "Unknown error");
        } finally {
            setSubmitting(false);
        }
    };

    const getCustomerName = (id: string) => {
        const customer = customers?.find(c => c.customer_id === id);
        return customer?.customer_name || id;
    };

    return (
        <div className="p-6">
            {/* Page header */}
            <div className="mb-6">
                <h1 className="text-h2 text-text-primary">User Management</h1>
                <p className="text-body-sm text-text-secondary mt-1">
                    Manage user accounts across all customers
                </p>
            </div>

            {(usersError || customersError) && (
                <div className="mb-4 rounded-lg bg-health-500/10 border border-health-500/30 p-3 text-body-sm text-health-400">
                    {((usersError || customersError) as Error).message}
                </div>
            )}
            {submitError && (
                <div className="mb-4 rounded-lg bg-health-500/10 border border-health-500/30 p-3 text-body-sm text-health-400">
                    {submitError}
                </div>
            )}

            {/* Add User Button */}
            <div className="mb-6">
                <button
                    onClick={() => setIsOpen(true)}
                    className="btn-primary flex items-center gap-2"
                >
                    <FiPlus /> Add New User
                </button>
            </div>

            {isLoadingUsers ? (
                <div className="card">
                    <div className="space-y-3">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-12 bg-surface-600 animate-pulse rounded" />
                        ))}
                    </div>
                </div>
            ) : (
                <div className="card overflow-hidden p-0">
                    {/* Table header */}
                    <div className="grid grid-cols-12 bg-surface-600 p-4 border-b border-border text-body-sm font-medium text-text-secondary">
                        <div className="col-span-2">Username</div>
                        <div className="col-span-3">Email</div>
                        <div className="col-span-2">Organization</div>
                        <div className="col-span-1">Role</div>
                        <div className="col-span-1">2FA</div>
                        <div className="col-span-1">Status</div>
                        <div className="col-span-2">Actions</div>
                    </div>

                    {/* User rows */}
                    {users && users.length > 0 ? (
                        <div className="divide-y divide-border">
                            {users.map(user => (
                                <div key={user.user_id} className="grid grid-cols-12 p-4 hover:bg-surface-600 items-center text-body-sm">
                                    <div className="col-span-2 flex items-center gap-2 text-text-primary">
                                        {user.is_admin ? <FiUserCheck className="text-quantum-400" /> : <FiUser className="text-text-muted" />}
                                        <span className="truncate">{user.full_name || user.username}</span>
                                    </div>
                                    <div className="col-span-3 text-text-secondary truncate">{user.email}</div>
                                    <div className="col-span-2 text-text-secondary truncate">{user.customer_id ? getCustomerName(user.customer_id) : "\u2014"}</div>
                                    <div className="col-span-1">
                                        <div className="flex flex-wrap gap-1">
                                            <span className={`px-2 py-0.5 rounded text-caption font-medium ${
                                                user.is_admin
                                                    ? 'bg-health-500/20 text-health-400'
                                                    : 'bg-ecosystem-500/20 text-ecosystem-400'
                                            }`}>
                                                {user.is_admin ? 'Admin' : 'User'}
                                            </span>
                                            {user.roles?.map(r => (
                                                <span key={r.role_id} className="px-2 py-0.5 rounded text-caption font-medium bg-quantum-500/20 text-quantum-400">
                                                    {r.role_name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="col-span-1">
                                        {user.totp_enabled ? (
                                            <FiShield className="w-4 h-4 text-ecosystem-400" title="2FA enabled" />
                                        ) : (
                                            <span className="text-text-muted">\u2014</span>
                                        )}
                                    </div>
                                    <div className="col-span-1">
                                        {!user.is_active ? (
                                            <span className="px-2 py-0.5 rounded text-caption font-medium bg-surface-500 text-text-muted">
                                                Inactive
                                            </span>
                                        ) : user.needs_password_reset ? (
                                            <span className="px-2 py-0.5 rounded text-caption font-medium bg-economic-500/20 text-economic-400">
                                                Pending
                                            </span>
                                        ) : (
                                            <span className="px-2 py-0.5 rounded text-caption font-medium bg-quantum-500/20 text-quantum-400">
                                                Active
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-span-2 flex items-center gap-2">
                                        {user.needs_password_reset && user.is_active && (
                                            <button
                                                onClick={() => handleResendInvite(user.user_id)}
                                                disabled={resendingId === user.user_id}
                                                className="flex items-center gap-1 text-caption text-quantum-400 hover:text-quantum-300 disabled:opacity-50"
                                                title="Resend invite email"
                                            >
                                                <FiSend className="w-3.5 h-3.5" />
                                                {resendingId === user.user_id ? "..." : resendSuccess === user.user_id ? "Sent!" : "Resend"}
                                            </button>
                                        )}
                                        {!user.needs_password_reset && user.is_active && (
                                            <button
                                                onClick={() => handleResetPassword(user.user_id)}
                                                disabled={resetPasswordId === user.user_id}
                                                className="flex items-center gap-1 text-caption text-economic-400 hover:text-economic-300 disabled:opacity-50"
                                                title="Send password reset email"
                                            >
                                                <FiKey className="w-3.5 h-3.5" />
                                                {resetPasswordId === user.user_id ? "..." : resetPasswordSuccess === user.user_id ? "Sent!" : "Reset"}
                                            </button>
                                        )}
                                        {confirmDeleteId === user.user_id ? (
                                            <span className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleDeleteUser(user.user_id)}
                                                    disabled={deletingId === user.user_id}
                                                    className="text-caption text-health-400 hover:text-health-300 disabled:opacity-50"
                                                >
                                                    {deletingId === user.user_id ? "..." : "Confirm"}
                                                </button>
                                                <button
                                                    onClick={() => setConfirmDeleteId(null)}
                                                    className="text-caption text-text-muted hover:text-text-secondary"
                                                >
                                                    Cancel
                                                </button>
                                            </span>
                                        ) : (
                                            <button
                                                onClick={() => setConfirmDeleteId(user.user_id)}
                                                className="text-text-muted hover:text-health-400"
                                                title="Delete user"
                                            >
                                                <FiTrash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-6 text-center text-text-muted">
                            No users found. Click "Add New User" to create one.
                        </div>
                    )}
                </div>
            )}

            {isFetchingUsers && !isLoadingUsers && (
                <p className="mt-2 text-caption italic text-text-muted">Updating list...</p>
            )}

            {/* Create User Modal */}
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="w-full max-w-lg rounded-xl bg-surface-700 border border-border p-6 shadow-xl">
                        <h3 className="mb-4 text-h4 text-text-primary">Add New User</h3>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Email field */}
                            <div>
                                <label className="input-label">Email *</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-muted">
                                        <FiMail />
                                    </div>
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="input pl-10"
                                        placeholder="john.doe@example.com"
                                    />
                                </div>
                            </div>

                            {/* Full Name field */}
                            <div>
                                <label className="input-label">Full Name</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-muted">
                                        <FiUser />
                                    </div>
                                    <input
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        className="input pl-10"
                                        placeholder="John Doe"
                                    />
                                </div>
                            </div>

                            {/* Admin checkbox */}
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="is_admin"
                                    checked={isAdmin}
                                    onChange={(e) => setIsAdmin(e.target.checked)}
                                    className="h-4 w-4 rounded border-border bg-surface-600 text-quantum-500 focus:ring-quantum-500"
                                />
                                <label htmlFor="is_admin" className="ml-2 block text-body-sm text-text-secondary">
                                    Admin user (full platform access)
                                </label>
                            </div>

                            {/* Organization selection field */}
                            <div>
                                <label className="input-label">Organization *</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-muted">
                                        <FiUsers />
                                    </div>
                                    {isAdmin ? (
                                        <select
                                            value={QUANTUM_ORG_ID}
                                            disabled={true}
                                            className="input pl-10 opacity-60 cursor-not-allowed"
                                        >
                                            <option value={QUANTUM_ORG_ID}>
                                                Quantum Energy (Admin Organization)
                                            </option>
                                        </select>
                                    ) : (
                                        <select
                                            required
                                            value={customerId}
                                            onChange={(e) => setCustomerId(e.target.value)}
                                            className="input pl-10"
                                            disabled={isLoadingCustomers || isAdmin}
                                        >
                                            <option value="">Select an organization</option>
                                            {customers?.map(customer => (
                                                <option key={customer.customer_id} value={customer.customer_id}>
                                                    {customer.customer_name}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                {isAdmin && (
                                    <p className="mt-1 text-caption text-text-muted">
                                        Admin users are automatically assigned to Quantum Energy
                                    </p>
                                )}
                            </div>

                            <p className="text-caption text-text-muted">
                                An invite email will be sent with a link to set their password.
                            </p>

                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="btn-secondary"
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary flex items-center gap-2"
                                    disabled={submitting}
                                >
                                    <FiSend /> {submitting ? "Sending..." : "Send Invite"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
